import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import clsx from 'clsx'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import ErrorComponent from '@/components/error/error-component'
import { useHubspotConversations } from '@/context/useHubspotConversations'
import { PopoverContainerContext } from '@/context/popover-container-context'
import { useTableCellScroll } from '@/medical-common/hooks/use-table-cell-scroll'
import type { CellPosition } from '@/medical-common/hooks/use-table-cell-scroll'
import { useViewportSizeInPixels } from '@/hooks/use-viewport-size-in-pixels'
import { CONTENT_VIEWER_COLUMN_TRANSITION_DURATION_MS } from '@/medical-common/constants'
import { DEVICE_TYPE_BREAKPOINTS } from '@/hooks/useDeviceType'
import { useMatterAssetFeatureConfig, useMatterAssetStoreKey } from '@/matter-asset-common/feature/matter-asset-feature-context'
import type { MatterAssetEventRow } from '@/matter-asset-common/feature/matter-asset-feature-config'

// Header row height in pixels
const HEADER_HEIGHT_PX = 42

// Only add spacer column when extra space in the table container exceeds this threshold (in pixels)
const MIN_SPACER_WIDTH_THRESHOLD = 25

/**
 * Helper to get column width styles
 * Spacer column expands to fill available space, regular columns use fixed widths
 */
const getColumnWidthProps = (size: number, isSpacer: boolean) => ({
  width: isSpacer ? '100%' : `${size}px`,
  minWidth: isSpacer ? '0' : `${size}px`,
  maxWidth: isSpacer ? 'none' : `${size}px`,
})

/**
 * Calculate the width available to table columns inside the scroll viewport.
 * Measure the viewport rather than the self-sizing inner wrapper so an existing
 * spacer column cannot keep itself alive after the viewport narrows.
 */
const getAvailableTableWidth = (scrollableElement: HTMLElement, tableContainerElement: HTMLElement) => {
  const style = window.getComputedStyle(tableContainerElement)
  const paddingLeft = parseFloat(style.paddingLeft) || 0
  const paddingRight = parseFloat(style.paddingRight) || 0
  return Math.max(0, scrollableElement.clientWidth - paddingLeft - paddingRight)
}

interface MatterRecordEventsTableProps {
  variant?: string
}

/**
 * Matter Asset Events Table
 *
 * Renders a virtualized table of a matter's living-asset events. The scaffolding (virtualization,
 * sticky header, spacer column, auto-scroll, loading/error/empty states) is feature-agnostic; the
 * column set, data selector, copy, and retry action come from the active feature config supplied
 * via MatterAssetFeatureContext, keyed by the asset store key the surrounding shell owns.
 */
export default function MatterRecordEventsTable(props: MatterRecordEventsTableProps) {
  const { variant } = props

  // The living asset shell provides the asset-keyed scope + config and owns data loading.
  const config = useMatterAssetFeatureConfig()
  const EventsLoadingSkeleton = config.EventsLoadingSkeleton
  const storeKey = useMatterAssetStoreKey()
  const dispatch = useAppDispatch()
  const { toggleWidget } = useHubspotConversations()

  // Redux state
  const eventsList = useAppSelector((state: RootState) => config.selectUIEventsByStoreKey(state, { storeKey }))
  const scopedDataError = useAppSelector((state: RootState) => config.selectDataError(state, { storeKey }))

  // Stable empty array to avoid creating new references when eventsList is null
  const emptyArray = useRef<MatterAssetEventRow[]>([])
  const tableData = eventsList ?? emptyArray.current

  // Scroll container for the virtualizer and popover context
  const [scrollableContainerElement, setScrollableContainerElement] = useState<HTMLDivElement | null>(null)

  // Inner table container whose responsive padding is excluded from the spacer calculation
  const [tableContainerElement, setTableContainerElement] = useState<HTMLDivElement | null>(null)
  const [availableTableWidth, setAvailableTableWidth] = useState(0)

  // Determine layout: desktop shows content viewer as side column, mobile shows as slideover modal
  const { width: viewportWidth } = useViewportSizeInPixels()
  const isColumnLayout = viewportWidth >= DEVICE_TYPE_BREAKPOINTS.DESKTOP

  // Set up auto-scroll: when a cell is clicked, scroll it into view if using column layout
  // This prevents the content viewer column from covering the clicked cell
  const scrollToCell = useTableCellScroll(scrollableContainerElement, {
    rows: tableData,
    delayMs: CONTENT_VIEWER_COLUMN_TRANSITION_DURATION_MS,
  })
  const handleScroll = useCallback(
    (position: CellPosition) => {
      // Only scroll in column layout (desktop). In slideover layout (mobile), no scroll needed
      // since the content viewer takes over the full screen
      if (!isColumnLayout) return
      scrollToCell(position)
    },
    [isColumnLayout, scrollToCell]
  )

  const columnHelper = useMemo(() => createColumnHelper<MatterAssetEventRow>(), [])

  // Feature-provided data columns (no spacer; the spacer is appended below once the width is known).
  const dataColumns = useMemo(() => config.buildEventColumns({ handleScroll, variant }), [config, handleScroll, variant])

  const columns = useMemo(() => {
    const filteredColumns = [...dataColumns]

    // Calculate total width of all data columns
    const tableDataColumnsWidth = filteredColumns.reduce((sum, col) => sum + (col.size || 0), 0)

    /*
     * When the table is narrower than the container, add a spacer column to fill the remaining space
     *
     * UX Goal: Avoid changes in column size when users switch between different views with different column counts
     * so that it's easier to see which columns are "new", without introducing pure blank space to the layout
     */
    const needsSpacer = availableTableWidth - tableDataColumnsWidth > MIN_SPACER_WIDTH_THRESHOLD
    if (needsSpacer && availableTableWidth > 0) {
      filteredColumns.push(
        columnHelper.display({
          id: 'spacer',
          header: '',
          cell: () => null,
          size: 0, // Fixed width not needed - getColumnWidthProps applies width: 100% to expand
          meta: { spacer: true },
        })
      )
    }

    return filteredColumns
  }, [availableTableWidth, columnHelper, dataColumns])

  // Memoize getRowId to prevent unnecessary table re-initialization
  const getRowId = useCallback((row: MatterAssetEventRow) => row.id, [])

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: false,
    getRowId,
  })

  // Virtualization config
  const { rows } = table.getRowModel()

  // Memoize virtualizer options to prevent unnecessary re-initialization
  // Without this, inline callbacks create new references on each render,
  // causing useVirtualizer to think the config changed and re-initialize
  const virtualizerOptions = useMemo(
    () => ({
      count: rows.length,
      getScrollElement: () => scrollableContainerElement,
      estimateSize: () => 200,
      overscan: 8,
      getItemKey: (index: number) => rows[index]?.id,
      measureElement: (el: HTMLElement) => el.getBoundingClientRect().height,
    }),
    [rows, scrollableContainerElement]
  )

  const rowVirtualizer = useVirtualizer(virtualizerOptions)

  // The virtualized window
  const virtualItems = rowVirtualizer.getVirtualItems()
  const totalSize = rowVirtualizer.getTotalSize()

  // Spacers simulate the height of unmounted rows above and below the window
  const padTop = virtualItems.length ? virtualItems[0].start : 0
  const padBot = virtualItems.length ? totalSize - virtualItems[virtualItems.length - 1].end : 0

  /**
   * Track the visible scroll viewport width for spacer column calculation
   * - The spacer column is conditionally added in the columns based on this width
   * - Updates on mount and on resize
   */
  useEffect(() => {
    if (!scrollableContainerElement || !tableContainerElement) return

    const updateAvailableWidth = () => {
      setAvailableTableWidth(getAvailableTableWidth(scrollableContainerElement, tableContainerElement))
    }

    // Initial measurement
    updateAvailableWidth()

    // Re-measure on resize with debounce
    let resizeTimeout: NodeJS.Timeout
    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(updateAvailableWidth, 100)
    })
    resizeObserver.observe(scrollableContainerElement)

    return () => {
      clearTimeout(resizeTimeout)
      resizeObserver.disconnect()
    }
  }, [scrollableContainerElement, tableContainerElement])

  // The shell loads data via the asset listeners. A null list means the refresh is in
  // flight - unless it failed, which gets an inline retry.
  if (eventsList === null) {
    if (scopedDataError) {
      return (
        <ErrorComponent
          code="500"
          title={config.copy.eventsErrorTitle}
          message={
            <>
              {'Our servers ran into a problem. Please '}
              <button
                onClick={() => storeKey && config.onRetryLoadEvents(dispatch, { storeKey })}
                className="text-brand-500 hover:text-brand-400 underline cursor-pointer"
              >
                try again
              </button>
              {', or '}
              <button onClick={toggleWidget} className="text-brand-500 hover:text-brand-400 underline cursor-pointer">
                contact support
              </button>
              {' if the issue continues.'}
            </>
          }
        />
      )
    }
    return <EventsLoadingSkeleton />
  }

  if (eventsList.length === 0) {
    return (
      <div className="p-24 flex items-center justify-center">
        <p className="text-brand-neutral-600">{config.copy.emptyEventsMessage}</p>
      </div>
    )
  }

  return (
    <PopoverContainerContext.Provider value={scrollableContainerElement}>
      <div className="flex-1 flex flex-col min-h-0 relative">
        {/* Overlay between the rounded sticky header and scrolling body hides the left and right borders that peek through as the rows scroll up */}
        <div className="absolute top-0 left-2 right-2 lg:left-4 lg:right-4 h-8 bg-brand-neutral-50 z-30 pointer-events-none" />

        {/* Scrollable container */}
        <div
          ref={setScrollableContainerElement}
          className="flex-1 min-h-0 mt-2 lg:mt-4 overflow-auto overscroll-none"
          style={{
            // Top scroll padding accounts for the sticky header height (use inline styling so we can use the constant value)
            scrollPaddingTop: `${HEADER_HEIGHT_PX}px`,
          }}
        >
          {/* Padding goes here instead of the scroll container because Safari can collapse right padding on overflow-auto */}
          <div ref={setTableContainerElement} className="inline-block min-w-full px-2 pb-2 lg:px-4 lg:pb-4">
            {/* Table */}
            <table className="border-separate border-spacing-0">
              {/* Sticky header */}
              <thead className="bg-brand-neutral-50 sticky top-0 z-40">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const isFirstCell = header.index === 0
                      const isLastCell = header.index === headerGroup.headers.length - 1
                      return (
                        <th
                          key={header.id}
                          className={clsx(
                            'text-left text-sm text-black tracking-[-0.15px] border-r border-b border-t font-medium px-4 py-2',
                            isFirstCell && 'rounded-tl-md border-l',
                            isLastCell && 'rounded-tr-md'
                          )}
                          // Set the header cell width to the column’s computed size
                          // Without this, the header and body can size themselves differently and the columns won’t align
                          style={getColumnWidthProps(header.getSize(), !!header.column.columnDef.meta?.spacer)}
                        >
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      )
                    })}
                  </tr>
                ))}
              </thead>

              {/* Table body */}
              <tbody>
                {/* Top spacer to represent offscreen rows above - spacer rows represent the height of all the unmounted rows above/below the
                    visible window. Required for virtualization so the scroll height behaves as if the full table is rendered. */}
                {padTop > 0 && (
                  <tr style={{ height: padTop }} aria-hidden>
                    <td colSpan={columns.length} />
                  </tr>
                )}

                {/* Only the visible rows are actually mounted */}
                {virtualItems.map((vi) => {
                  const row = rows[vi.index]
                  const isLastRow = vi.index === rows.length - 1
                  return (
                    <tr key={row.id} data-index={vi.index} ref={rowVirtualizer.measureElement}>
                      {row.getVisibleCells().map((cell, cellIndex) => {
                        const isFirstCell = cellIndex === 0
                        const isLastCell = cellIndex === row.getVisibleCells().length - 1
                        return (
                          <td
                            key={cell.id}
                            id={cell.id}
                            className={clsx(
                              'bg-white text-sm align-top border-r border-b',
                              isFirstCell && 'border-l',
                              isLastRow && isFirstCell && 'rounded-bl-md',
                              isLastRow && isLastCell && 'rounded-br-md',
                              cell.column.columnDef.meta?.hoverable && 'relative'
                            )}
                            // Apply the same fixed width as the header.
                            // Prevents column shifts or misalignment caused by virtualization only rendering some rows.
                            style={getColumnWidthProps(cell.column.getSize(), !!cell.column.columnDef.meta?.spacer)}
                          >
                            <div className="h-full w-full break-words">{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}

                {/* Bottom spacer to represent offscreen rows below */}
                {padBot > 0 && (
                  <tr style={{ height: padBot }} aria-hidden>
                    <td colSpan={columns.length} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </PopoverContainerContext.Provider>
  )
}
